import { StopOutlined } from '@ant-design/icons'
import { usePermissions } from '@refinedev/core'
import { Alert, Breadcrumb, Button, Card, Space } from 'antd'
import { useState } from 'react'

import { gpuAdminConfigured } from '@/api/gpu/client'
import { hasPermission } from '@/auth/capabilities'

import { GpuState } from '../components/GpuState'
import { ReasonModal } from '../components/ReasonModal'
import { RulesTable } from '../components/RulesTable'
import { useAkashBlockChange, useAkashBlocks } from '../hooks/useGpuPolicies'
import { BlockHistory } from './BlockHistory'

// bech32 charset after the human-readable part; the service checks the sum.
const AKASH_OWNER = /^akash1[02-9ac-hj-np-z]{38}$/

export default function AkashBlocksListPage() {
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canWrite =
    gpuAdminConfigured && hasPermission(permissions, 'admin:gpu:write')
  const { data, isLoading, error } = useAkashBlocks()
  const { block, unblock } = useAkashBlockChange()
  const [open, setOpen] = useState(false)

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: 'GPU' }, { title: 'Akash provider blocks' }]}
      />
      <GpuState error={error} />
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Akash leases go only to audited providers; bids from blocked providers are refused. Running leases are not affected."
        />
        {canWrite && (
          <Button icon={<StopOutlined />} onClick={() => setOpen(true)}>
            Block a provider
          </Button>
        )}
        <RulesTable
          rows={data?.map((b) => ({ ...b, key: b.owner }))}
          loading={isLoading}
          error={error}
          keyTitle="Provider owner"
          emptyText="No blocked providers"
          canWrite={canWrite}
          removeAction="Unblock"
          onRemove={(owner) => unblock.mutateAsync({ key: owner })}
        />
        <Card size="small" title="Block history">
          <BlockHistory />
        </Card>
      </Space>
      <ReasonModal
        open={open}
        title="Block an Akash provider"
        action="Block"
        target={{
          label: 'Provider owner address',
          placeholder: 'akash1…',
          normalize: (v: string) => v.trim(),
          rules: [
            { required: true, message: 'Enter the owner address' },
            { pattern: AKASH_OWNER, message: 'Not an akash1… address' },
          ],
        }}
        maxReason={2048}
        existing={data?.map((b) => b.owner)}
        loading={block.isPending}
        onSubmit={(key, reason) => block.mutateAsync({ key, reason })}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}
